import type { SceneNode, Transform } from "../scene/model";

/** A command needs only the node's transform, never its bitmap or renderer. */
export type TransformNode = Pick<SceneNode, "id" | "transform">;
export interface NodeBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}
export type Alignment =
  "left" | "center-x" | "right" | "top" | "center-y" | "bottom";
export type TransformCommand =
  | { type: "translate"; x: number; y: number }
  | { type: "align"; alignment: Alignment }
  | { type: "distribute"; axis: "x" | "y" };

export function nudgeCommandForKey(
  key: string,
  accelerated = false,
): TransformCommand | null {
  const step = accelerated ? 10 : 1;
  switch (key) {
    case "ArrowLeft":
      return { type: "translate", x: -step, y: 0 };
    case "ArrowRight":
      return { type: "translate", x: step, y: 0 };
    case "ArrowUp":
      return { type: "translate", x: 0, y: -step };
    case "ArrowDown":
      return { type: "translate", x: 0, y: step };
    default:
      return null;
  }
}

export function alignmentCommand(mode: string): TransformCommand | null {
  if (mode === "distribute-x" || mode === "distribute-y")
    return { type: "distribute", axis: mode === "distribute-x" ? "x" : "y" };
  if (["left", "center-x", "right", "top", "center-y", "bottom"].includes(mode))
    return { type: "align", alignment: mode as Alignment };
  return null;
}

function finite(value: number) {
  if (!Number.isFinite(value))
    throw new Error("Некорректная геометрия команды.");
  return value;
}

function translated<T extends TransformNode>(node: T, x: number, y: number): T {
  const beforeX = finite(node.transform.x ?? 0);
  const beforeY = finite(node.transform.y ?? 0);
  const afterX = finite(beforeX + x),
    afterY = finite(beforeY + y);
  if (afterX === beforeX && afterY === beforeY) return node;
  const transform: Transform = { ...node.transform };
  // Preserve absent/unchanged axes and every non-position field.
  if (afterX !== beforeX) transform.x = afterX;
  if (afterY !== beforeY) transform.y = afterY;
  return { ...node, transform };
}

/** Pure operation on scene values. Bounds are in document coordinates, including strokes. */
export function transformNodes<T extends TransformNode>(
  nodes: readonly T[],
  command: TransformCommand,
  bounds: ReadonlyMap<string, NodeBounds> = new Map(),
): readonly T[] {
  if (new Set(nodes.map((node) => node.id)).size !== nodes.length)
    throw new Error("Повторяющийся идентификатор цели команды.");
  if (command.type === "translate") {
    finite(command.x);
    finite(command.y);
    return nodes.map((node) => translated(node, command.x, command.y));
  }
  const minimum = command.type === "distribute" ? 3 : 2;
  if (nodes.length < minimum) return nodes;
  const entries = nodes.map((node) => {
    const rect = bounds.get(node.id);
    if (
      !rect ||
      !Object.values(rect).every(Number.isFinite) ||
      rect.width < 0 ||
      rect.height < 0
    )
      throw new Error("Отсутствуют корректные границы объекта.");
    return { node, rect };
  });
  if (command.type === "distribute") {
    const axis = command.axis,
      size = axis === "x" ? "width" : "height";
    // Stable order, including coincident edges. This is not a layer reorder.
    const sorted = entries.slice().sort((a, b) => a.rect[axis] - b.rect[axis]);
    const start = sorted[0]!.rect[axis],
      last = sorted[sorted.length - 1]!.rect;
    const occupied = sorted.reduce((sum, entry) => sum + entry.rect[size], 0);
    const gap = finite(
      (last[axis] + last[size] - start - occupied) / (sorted.length - 1),
    );
    const positions = new Map<string, T>();
    let cursor = start;
    for (const { node, rect } of sorted) {
      const delta = cursor - rect[axis];
      positions.set(
        node.id,
        translated(node, axis === "x" ? delta : 0, axis === "y" ? delta : 0),
      );
      cursor += rect[size] + gap;
    }
    return nodes.map((node) => positions.get(node.id)!);
  }
  const left = Math.min(...entries.map((e) => e.rect.x));
  const top = Math.min(...entries.map((e) => e.rect.y));
  const right = Math.max(...entries.map((e) => e.rect.x + e.rect.width));
  const bottom = Math.max(...entries.map((e) => e.rect.y + e.rect.height));
  return entries.map(({ node, rect }) => {
    switch (command.alignment) {
      case "left":
        return translated(node, left - rect.x, 0);
      case "center-x":
        return translated(
          node,
          (left + right) / 2 - (rect.x + rect.width / 2),
          0,
        );
      case "right":
        return translated(node, right - (rect.x + rect.width), 0);
      case "top":
        return translated(node, 0, top - rect.y);
      case "center-y":
        return translated(
          node,
          0,
          (top + bottom) / 2 - (rect.y + rect.height / 2),
        );
      case "bottom":
        return translated(node, 0, bottom - (rect.y + rect.height));
    }
  });
}
